import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiPenTool, FiMenu, FiX } = FiIcons

const Header = ({ onGenerateClick }) => {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const scrollToSection = (sectionId) => {
    // Close mobile menu first
    setIsMobileMenuOpen(false)
    
    // Add a small delay to ensure menu closes, then navigate
    setTimeout(() => {
      if (isHomePage) {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      } else {
        // Navigate to home page and scroll to section
        window.location.href = `/#${sectionId}`
      }
    }, 100)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <header 
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 w-full"
      style={{ position: 'sticky' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/logos/professional-signature-logo-PNG-Icon-Only-Black.png" 
              alt="Professional Signature Logo" 
              className="h-16 w-16"
            />
            <span className="hidden sm:block text-xl font-display font-bold text-gray-900">
              Professional Signature
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              How it Works
            </button>
            <button 
              onClick={() => scrollToSection('examples')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Examples
            </button>
            <button 
              onClick={() => scrollToSection('generate')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Generate
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              FAQ
            </button>
          </nav>

          {/* Desktop CTA Button */}
          <motion.button
            onClick={onGenerateClick}
            className="hidden md:block bg-primary-600 text-white px-6 py-2 rounded-full font-medium hover:bg-primary-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Try it Now
          </motion.button>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <nav className="py-4 space-y-1">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="block w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Home
                </button>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="block w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  How it Works
                </button>
                <button 
                  onClick={() => scrollToSection('examples')}
                  className="block w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Examples
                </button>
                <button 
                  onClick={() => scrollToSection('generate')}
                  className="block w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Generate
                </button>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="block w-full text-left px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  FAQ
                </button>
                <div className="px-4 py-3">
                  <motion.button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      setTimeout(() => {
                        onGenerateClick()
                      }, 100)
                    }}
                    className="w-full bg-primary-600 text-white px-6 py-3 rounded-full font-medium hover:bg-primary-700 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Try it Now
                  </motion.button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

export default Header